import type { Protocol, ProtocolTrigger } from './schema';
import { RI_PROTOCOLS } from './ri-protocols';

/**
 * Resolves which protocol(s) apply to the current ePCR based on
 * dispatch reason, chief complaint, primary impression, and vitals.
 */
export function resolveProtocol(context: {
  dispatchReason?: string | null;
  chiefComplaint?: string | null;
  primaryImpression?: string | null;
  vitals?: Record<string, number | null>;
  protocols?: Protocol[];
}): Protocol[] {
  const allProtocols = context.protocols ?? RI_PROTOCOLS;
  const matched: Protocol[] = [];

  for (const protocol of allProtocols) {
    for (const trigger of protocol.triggers) {
      if (matchesTrigger(trigger, context)) {
        matched.push(protocol);
        break; // One match per protocol is enough
      }
    }
  }

  return matched;
}

function matchesTrigger(
  trigger: ProtocolTrigger,
  context: {
    dispatchReason?: string | null;
    chiefComplaint?: string | null;
    primaryImpression?: string | null;
    vitals?: Record<string, number | null>;
  },
): boolean {
  switch (trigger.type) {
    case 'dispatch_reason':
      return fuzzyMatch(context.dispatchReason, trigger.value);
    case 'chief_complaint':
      return fuzzyMatch(context.chiefComplaint, trigger.value);
    case 'primary_impression':
      return fuzzyMatch(context.primaryImpression, trigger.value);
    case 'vitals_threshold': {
      if (!context.vitals || !trigger.vitalField || !trigger.vitalOperator || trigger.vitalValue === undefined) {
        return false;
      }
      const actual = context.vitals[trigger.vitalField];
      if (actual === null || actual === undefined) return false;
      return compareVital(actual, trigger.vitalOperator, trigger.vitalValue);
    }
    default:
      return false;
  }
}

function fuzzyMatch(input: string | null | undefined, trigger: string): boolean {
  if (!input) return false;
  const a = input.toLowerCase().trim();
  const b = trigger.toLowerCase().trim();
  return a === b || a.includes(b) || b.includes(a);
}

function compareVital(actual: number, operator: '<' | '>' | '<=' | '>=', threshold: number): boolean {
  switch (operator) {
    case '<': return actual < threshold;
    case '>': return actual > threshold;
    case '<=': return actual <= threshold;
    case '>=': return actual >= threshold;
  }
}
